import {
  dayKey,
  type CheckInTemplate,
  type Entry,
  type EntryQuery,
  type Tag,
  type Track,
} from '@anytracker/core'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { checkInColour, paletteVar } from '../../charts/colours.js'
import { Button } from '../../components/Button.js'
import { ICONS, Icon, Page, Tabs, type Choice } from '../../components/Layout.js'
import { formatDayHeading } from '../../format/dates.js'
import { DEFAULT_RANGE, useRange } from '../../range/RangeProvider.js'
import { writeRangeParams } from '../../range/url.js'
import { useServices } from '../../services/ServicesProvider.js'
import { EntryRow } from './EntryRow.js'
import { FilterSheet, activeFilterCount, type Filters } from './FilterSheet.js'
import styles from './Timeline.module.css'

/** A flat list of headers and entries, which is what makes virtualising this easy. */
type Row =
  | {
      readonly kind: 'day'
      readonly key: string
      readonly at: Entry['at']
      readonly count: number
    }
  | {
      readonly kind: 'entry'
      readonly key: string
      readonly entry: Entry
      /** Closes the day's band. */
      readonly last: boolean
    }

/** Which URL parameter carries which axis. */
const PARAM: Record<keyof Filters, string> = {
  trackId: 'track',
  tagId: 'tag',
  templateId: 'checkin',
}

/**
 * History, newest first (T-305, refitted in T-904).
 *
 * Grouped by the **captured** local day, not the reader's and not UTC: an entry
 * recorded at 23:30 in New York belongs to that day even though UTC has already
 * rolled over, and getting this wrong is precisely the corruption ADR-0006
 * exists to prevent.
 *
 * Each day is a heading on the page and a band of rows (the Meadow redesign).
 * The time leads every row in its own column, so a day reads as a day with a
 * shape — a cluster in the morning, a gap, one more at night.
 *
 * Check-ins are tabs across the top, because "only my evening check-ins" is
 * the filter people reach for most. A tab writes the same `checkin` parameter
 * the filter sheet does, so the two can never disagree and either is
 * bookmarkable.
 *
 * Virtualised from the outset. This list grows without bound by design, and
 * retrofitting virtualisation after the row component has grown measurement
 * logic is materially harder — which is why the ticket specified it up front.
 */
export function TimelineScreen() {
  const { repos, clock } = useServices()
  const navigate = useNavigate()
  const [search, setSearch] = useSearchParams()
  const { selection, resolved, setSelection } = useRange()

  const [entries, setEntries] = useState<Entry[] | null>(null)
  const [tracks, setTracks] = useState<Map<string, Track>>(new Map())
  const [tags, setTags] = useState<Map<string, Tag>>(new Map())
  const [templates, setTemplates] = useState<Map<string, CheckInTemplate>>(new Map())
  const [totalEver, setTotalEver] = useState(0)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Filters live in the URL, so a filtered view is bookmarkable and survives a
  // refresh — and so the tag screen can link straight into one (T-306). That
  // matters more now they are behind a button: a sheet hides the state, so it
  // has to be legible in the address bar and in the chips below.
  const filters: Filters = {
    trackId: search.get(PARAM.trackId) ?? '',
    tagId: search.get(PARAM.tagId) ?? '',
    templateId: search.get(PARAM.templateId) ?? '',
  }

  /**
   * "All time" is no range filter at all, rather than the resolved bounds.
   *
   * The two are equivalent once the first and last entry have loaded, but the
   * bounds arrive a tick after the first render — long enough to flash a
   * timeline filtered to today — and anything logged since they were read would
   * fall outside them. The timeline's default is the whole of history, and this
   * keeps it exactly that.
   */
  const wholeHistory = selection.kind === 'preset' && selection.preset === 'allTime'

  const { trackId, tagId, templateId } = filters
  const query = useMemo((): EntryQuery => {
    const range = wholeHistory
      ? undefined
      : {
          fromEpochMs: resolved.fromEpochMs,
          // The last millisecond of the end day, not its start — otherwise "to
          // the 5th" silently excludes everything recorded on the 5th.
          toEpochMs: resolved.toEpochMs,
        }
    return {
      ...(range === undefined ? {} : { range }),
      ...(trackId === '' ? {} : { trackId }),
      ...(tagId === '' ? {} : { tagId }),
      ...(templateId === '' ? {} : { templateId }),
    }
  }, [wholeHistory, resolved.fromEpochMs, resolved.toEpochMs, trackId, tagId, templateId])

  /*
   * Whether anything is narrowing the list — including the range.
   *
   * Wider than the chip count deliberately: the range is a filter, and it is
   * why the list is empty as often as a track or a tag is. Offering "Clear all
   * filters" only when a chip is showing leaves the one that is actually
   * hiding everything in place.
   */
  const narrowed = Object.keys(query).length > 0

  const reload = useCallback(async () => {
    const [found, allTracks, allTags, allTemplates, everything] = await Promise.all([
      repos.entries.find(query),
      // Archived included: a measurement against a retired track still has to
      // render, with the configuration it was recorded under — and it has to
      // stay filterable, or months of history become unreachable.
      repos.tracks.list({ includeArchived: true }),
      repos.tags.list(),
      repos.templates.list({ includeArchived: true }),
      // Counted, not read. The two empty states need to tell "nothing matches"
      // from "nothing exists", which is a number — and reading ten thousand
      // entries to find out how many there are is work this screen does on
      // every filter change.
      repos.entries.count(),
    ])
    setEntries(found)
    setTracks(new Map(allTracks.map((track) => [track.id, track])))
    setTags(new Map(allTags.map((tag) => [tag.id, tag])))
    setTemplates(new Map(allTemplates.map((template) => [template.id, template])))
    setTotalEver(everything)
  }, [repos, query])

  useEffect(() => {
    void reload()
  }, [reload])

  /** Newest first, flattened to day headers plus entries. */
  const rows = useMemo((): Row[] => {
    if (!entries) {
      return []
    }
    const byDay = new Map<string, Entry[]>()
    for (const entry of entries) {
      const key = dayKey(entry.at)
      const bucket = byDay.get(key)
      if (bucket) {
        bucket.push(entry)
      } else {
        byDay.set(key, [entry])
      }
    }

    const out: Row[] = []
    for (const key of [...byDay.keys()].sort().reverse()) {
      const forDay = byDay.get(key) ?? []
      const first = forDay[0]
      if (!first) {
        continue
      }
      out.push({ kind: 'day', key, at: first.at, count: forDay.length })
      const ordered = [...forDay].sort((a, b) => b.at.epochMs - a.at.epochMs)
      ordered.forEach((entry, index) => {
        out.push({
          kind: 'entry',
          key: entry.id,
          entry,
          last: index === ordered.length - 1,
        })
      })
    }
    return out
  }, [entries])

  const scroller = useRef<HTMLDivElement>(null)
  const virtual = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scroller.current,
    // A starting guess only; rows measure themselves, which they must because
    // an entry's height depends on how many measurements and tags it carries.
    estimateSize: (index) => (rows[index]?.kind === 'day' ? 52 : 88),
    overscan: 8,
  })

  function setFilter(axis: keyof Filters, value: string) {
    const next = new URLSearchParams(search)
    if (value === '') {
      next.delete(PARAM[axis])
    } else {
      next.set(PARAM[axis], value)
    }
    setSearch(next, { replace: true })
  }

  /**
   * Clears the range as well as the three axes.
   *
   * The range is a filter — it is why the list is empty as often as a track or
   * a tag is — and a "Clear filters" button that leaves one applied is a button
   * that appears not to work.
   */
  function clearFilters() {
    setSearch(new URLSearchParams(), { replace: true })
    setSelection(DEFAULT_RANGE)
  }

  /** Back to the whole of history, leaving the other filters alone. */
  function clearRange() {
    setSelection(DEFAULT_RANGE)
    setSearch(writeRangeParams(search, DEFAULT_RANGE), { replace: true })
  }

  const deviceZone = clock.timeZone()
  const now = clock.now()
  // The range counts: it lives in the sheet now, so the button is where its
  // presence has to show.
  const count = activeFilterCount(filters) + (wholeHistory ? 0 : 1)

  /** One chip per set axis, each clearing only itself. */
  const chips: { key: string; label: string; clear: () => void }[] = [
    ...(
      [
        ['trackId', tracks.get(filters.trackId)?.name],
        ['tagId', tags.get(filters.tagId)?.name],
        ['templateId', templates.get(filters.templateId)?.name],
      ] as const
    ).flatMap(([axis, label]) =>
      filters[axis] === '' || label === undefined
        ? []
        : [
            {
              key: axis,
              label,
              clear: () => {
                setFilter(axis, '')
              },
            },
          ],
    ),
    // The range is in the sheet, so it gets a chip like any other filter: a
    // window narrowed to a week must not be invisible on the page it narrows.
    ...(wholeHistory ? [] : [{ key: 'range', label: resolved.label, clear: clearRange }]),
  ]

  /*
   * One tab per check-in still in use, plus the archived one being looked at
   * if that is where a link landed — a tab bar that hides the current choice
   * is a filter nobody can see.
   */
  const tabs: Choice<string>[] = [
    { value: 'all', label: 'All', colour: 'var(--text)' },
    ...[...templates.values()]
      .filter((template) => !template.archived || template.id === templateId)
      .sort((a, b) => a.position - b.position)
      .map((template) => ({
        value: template.id,
        label: template.name,
        colour: paletteVar(checkInColour(template), 'deep'),
      })),
  ]

  // The check-in filter is visible as the chosen tab, and "All" clears it, so
  // a chip saying the same thing again is only shown when there are no tabs.
  const visibleChips =
    tabs.length > 1 ? chips.filter((chip) => chip.key !== 'templateId') : chips

  return (
    <Page>
      <div className={styles['header']}>
        <h1>Timeline</h1>
        <button
          type="button"
          className={styles['filterButton']}
          /*
            Named, not left to concatenation. The badge sits flush against the
            word, so the computed name would be "Filter2" — a string that reads
            as nothing to a screen reader and tells nobody what the number
            counts.
          */
          aria-label={count === 0 ? 'Filter' : `Filter, ${String(count)} active`}
          onClick={() => {
            setSheetOpen(true)
          }}
        >
          <Icon path={ICONS.filter} />
          {count > 0 && (
            <span className={styles['filterCount']} aria-hidden="true">
              {count}
            </span>
          )}
        </button>
      </div>

      {tabs.length > 1 && (
        <Tabs
          label="Check-in"
          choices={tabs}
          value={templateId === '' ? 'all' : templateId}
          onChange={(value) => {
            setFilter('templateId', value === 'all' ? '' : value)
          }}
        />
      )}

      {/*
        What is filtered, without reopening the sheet. A sheet hides the state
        it holds, so it has to be visible here or the screen is quietly lying
        about how much of history it is showing.
      */}
      {visibleChips.length > 0 && (
        <div className={styles['chips']}>
          {visibleChips.map(({ key, label, clear }) => (
            <span key={key} className={styles['chip']}>
              {label}
              <button
                type="button"
                className={styles['chipClear']}
                aria-label={`Clear filter: ${label}`}
                onClick={clear}
              >
                <Icon path={ICONS.close} />
              </button>
            </span>
          ))}
        </div>
      )}

      <FilterSheet
        open={sheetOpen}
        filters={filters}
        tracks={tracks}
        tags={tags}
        templates={templates}
        onChange={setFilter}
        onClearAll={clearFilters}
        canClear={narrowed}
        onClose={() => {
          setSheetOpen(false)
        }}
      />

      {entries === null ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className={styles['empty']}>
          {/*
            Two distinct empty states. "Nothing matches" and "nothing exists"
            call for completely different next actions, and showing the wrong
            one sends someone looking for data they never recorded.
          */}
          {totalEver === 0 ? (
            <>
              <p className={styles['emptyTitle']}>Nothing recorded yet</p>
              <p className={styles['emptyBody']}>
                Once you log a check-in it will appear here, newest first.
              </p>
              <Button variant="primary" onClick={() => void navigate('/')}>
                Log something
              </Button>
            </>
          ) : (
            <>
              <p className={styles['emptyTitle']}>Nothing matches these filters</p>
              <p className={styles['emptyBody']}>
                There{' '}
                {totalEver === 1 ? 'is 1 entry' : `are ${String(totalEver)} entries`}{' '}
                recorded — just none in this selection.
              </p>
              <Button variant="primary" onClick={clearFilters}>
                Clear filters
              </Button>
            </>
          )}
        </div>
      ) : (
        <div
          className={styles['scroller']}
          ref={scroller}
          data-testid="timeline-scroller"
          data-bleed
        >
          <div
            className={styles['viewport']}
            style={{ height: `${String(virtual.getTotalSize())}px` }}
          >
            {virtual.getVirtualItems().map((item) => {
              const row = rows[item.index]
              if (!row) {
                return null
              }
              return (
                <div
                  key={row.key}
                  className={styles['item']}
                  data-index={item.index}
                  ref={virtual.measureElement}
                  style={{ transform: `translateY(${String(item.start)}px)` }}
                >
                  {row.kind === 'day' ? (
                    <div
                      className={styles['day']}
                      data-today={formatDayHeading(row.at, now) === 'Today'}
                      data-testid="day-heading"
                      data-day={row.key}
                    >
                      {/* Named near the present, dated once a name stops
                          identifying a day — and read in the entry's own zone,
                          so a day logged abroad keeps its own heading. */}
                      <span className={styles['dayLabel']}>
                        {formatDayHeading(row.at, now)}
                      </span>
                      <span className={styles['dayCount']}>
                        {row.count === 1 ? '1 entry' : `${String(row.count)} entries`}
                      </span>
                    </div>
                  ) : (
                    <EntryRow
                      entry={row.entry}
                      tracks={tracks}
                      tags={tags}
                      templates={templates}
                      deviceZone={deviceZone}
                      last={row.last}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </Page>
  )
}
